/* @flow */
import { css, names } from 'linaria';
import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import type { History } from 'react-router';

import { Button, Icon, NavLink } from '../core';
import Colors from '../../constants/Colors';
import currentUserRedux from '../../../redux/current-user';
import MediaQueries from '../../constants/MediaQueries';
import withBreakpoint from '../withBreakpoint';
import withWindowDimensions from '../withWindowDimensions';
import withCurrentUser from '../../components/withCurrentUser';
import AnimatedSiteServicesMenu from './AnimatedSiteServicesMenu';
import UserMenu from './UserMenu';
import UserAgent from '../../../utilities/UserAgent';
import Links from '../../../data/Links';

const LinkItem = withBreakpoint(
  ({
    children,
    to,
    className,
    activeClassName,
    breakpoint,
    verticalBreakpoint,
    retina,
    ...props
  }: {
    children?: any,
    to?: string,
    className?: string,
    activeClassName?: string,
    breakpoint: BreakpointSize,
    retina: boolean,
  }) => {
    return (
      <NavLink
        {...props}
        to={to}
        className={names(breakpoint === 'small' ? Styles.MobileLink : Styles.Link, className)}
        activeClassName={names(
          breakpoint === 'small' ? Styles.MobileActiveLink : Styles.ActiveLink,
          activeClassName
        )}>
        {children}
      </NavLink>
    );
  }
);

class SiteNavigation extends Component<
  {
    breakpoint: BreakpointSize,
    windowDimensions: { width: number, height: number },
    theme: 'light' | 'dark',
    currentUser: ?Object,
    logout: () => void,
    history: History,
  },
  {
    servicesMenuShowing: boolean,
    servicesLinkElement: ?HTMLElement,
  }
> {
  servicesMenu: AnimatedSiteServicesMenu;
  servicesLinkCloseTimeout: any;
  serviceLinkElement: HTMLElement;
  state = {
    servicesMenuShowing: false,
    servicesLinkElement: null,
  };

  _onClickServicesLink = e => {
    e.preventDefault();
    this.setState({ servicesLinkElement: e.target }, () => {
      this.servicesMenu.toggle(true);
    });
  };

  _onClickDownloadButton = e => {
    e.preventDefault();
    if (UserAgent.ios) {
      window.location = Links.AppleAppStore;
    } else if (UserAgent.android) {
      window.location = Links.GooglePlayStore;
    }
  };

  _onRequestCloseModal = () => {};

  _onLogOut = () => {
    const { history, logout } = this.props;
    logout();
    history.push('/');
  };

  _onMouseEnterServicesLink = e => {
    this.setState({ servicesLinkElement: e.target }, () => {
      this.servicesMenu.show();
    });
  };

  _onMouseLeaveServicesLink = () => {
    this.servicesMenu.hide();
  };

  _onSiteServicesMenuShow = () => {
    this.setState({ servicesMenuShowing: true });
  };

  _onSiteServicesMenuHide = () => {
    this.setState({ servicesMenuShowing: false });
  };

  renderServicesListItem() {
    let { theme, breakpoint } = this.props;
    let { servicesMenuShowing } = this.state;

    return (
      <li className={Styles.ListItem}>
        <LinkItem
          to="/services"
          className={names(ThemeStyles[theme].Link, breakpoint === 'small' && Styles.ServicesLink)}
          activeClassName={names(
            ThemeStyles[theme].ActiveLink,
            breakpoint === 'small' && Styles.ServicesLink
          )}
          onMouseEnter={this._onMouseEnterServicesLink}
          onMouseLeave={this._onMouseLeaveServicesLink}
          onTouchEnd={this._onClickServicesLink}>
          Services{' '}
          {breakpoint === 'small' && (
            <Icon
              name={theme === 'dark' ? 'chevron-up-white' : 'chevron-up-indigo'}
              style={{
                transition: 'transform 200ms ease',
                transform: `rotate(${servicesMenuShowing ? 0 : 180}deg)`,
              }}
            />
          )}
        </LinkItem>
        <AnimatedSiteServicesMenu
          onShow={this._onSiteServicesMenuShow}
          onHide={this._onSiteServicesMenuHide}
          popoverTarget={this.state.servicesLinkElement}
          componentRef={ref => (this.servicesMenu = ref)}
        />
      </li>
    );
  }

  render() {
    let { theme, breakpoint, currentUser } = this.props;

    return (
      <nav className={Styles.Container}>
        <ul className={Styles.List}>
          {currentUser && breakpoint === 'small' && (
            <li className={Styles.ListItem}>
              <LinkItem
                to="/account/luggs"
                className={names(
                  ThemeStyles[theme].Link,
                  breakpoint === 'medium' ? Styles.LastListItem : null
                )}
                activeClassName={ThemeStyles[theme].ActiveLink}>
                My Account
              </LinkItem>
            </li>
          )}
          {this.renderServicesListItem()}
          <li className={Styles.ListItem}>
            <LinkItem
              to="/cities"
              className={ThemeStyles[theme].Link}
              activeClassName={ThemeStyles[theme].ActiveLink}>
              Cities
            </LinkItem>
          </li>
          {breakpoint !== 'medium' && (
            <li className={Styles.ListItem}>
              <LinkItem
                to="/partner"
                className={ThemeStyles[theme].Link}
                activeClassName={ThemeStyles[theme].ActiveLink}>
                Partner
              </LinkItem>
            </li>
          )}
          {breakpoint !== 'medium' && (
            <li className={Styles.ListItem}>
              <LinkItem
                to="/estimate"
                className={ThemeStyles[theme].Link}
                activeClassName={ThemeStyles[theme].ActiveLink}>
                Get an estimate
              </LinkItem>
            </li>
          )}
          {!currentUser && (
            <li className={names(Styles.ListItem, Styles.ButtonItem, Styles.LastListItem)}>
              <Button
                to="/book"
                theme={theme === 'dark' ? 'white' : 'violet'}
                className={Styles.Button}>
                Book now
              </Button>
            </li>
          )}
          {currentUser &&
            breakpoint === 'small' && [
              <li key="b" className={names(Styles.ListItem, Styles.ButtonItem)}>
                <Button to="/book" theme="yellow" bordered={false} className={Styles.Button}>
                  Book now
                </Button>
              </li>,
              <li key="l" className={names(Styles.ListItem, Styles.ButtonItem)}>
                <Button
                  onClick={this._onLogOut}
                  theme="red"
                  bordered={false}
                  className={Styles.Button}>
                  Log out
                </Button>
              </li>,
            ]}
          {breakpoint !== 'small' && <UserMenu theme={theme} onLogOut={this._onLogOut} />}
        </ul>
      </nav>
    );
  }
}

const ThemeStyles = {
  light: {
    Link: css`
      color: ${Colors.Indigo};
      &:hover {
        color: ${Colors.opacity(Colors.Indigo, 0.5)};
      }
    `,
    ActiveLink: css`
      color: ${Colors.Indigo};
      &:hover {
        color: ${Colors.opacity(Colors.Indigo, 0.5)};
      }
      &:after {
        background-color: ${Colors.Violet};
      }
    `,
  },
  dark: {
    Link: css`
      color: ${Colors.White};
      &:hover {
        color: ${Colors.opacity(Colors.White, 0.5)};
      }
    `,
    ActiveLink: css`
      color: ${Colors.White};
      &:hover {
        color: ${Colors.opacity(Colors.White, 0.5)};
      }
      &:after {
        background-color: ${Colors.Yellow};
      }
    `,
  },
};

const Styles = {
  Container: css`
    ${MediaQueries.Medium} {
      display: flex;
      justify-content: flex-end;
    }
  `,
  List: css`
    ${MediaQueries.Medium} {
      display: flex;
    }
  `,
  ListItem: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    margin-right: 0;
    margin-top: 0;
    ${MediaQueries.Medium} {
      margin-right: 40px;
    }
    ${MediaQueries.Large} {
      margin-right: 56px;
    }
  `,
  LastListItem: css`
    margin-right: 0;
    ${MediaQueries.Medium} {
      margin-right: 0;
    }
    ${MediaQueries.Large} {
      margin-right: 0;
    }
  `,
  ButtonItem: css`
    margin-right: 12px;
    padding: 12px 24px;
    ${MediaQueries.Large} {
      margin-right: 12px;
      padding: 12px 0;
    }
  `,
  MobileLink: css`
    width: 100%;
    padding: 12px 32px;
    font-size: 20px;
  `,
  Link: css`
    -webkit-tap-highlight-color: transparent;
    transition: color 100ms ease;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    font-size: 18px;
    height: 100%;
    font-weight: 700;
  `,
  ActiveLink: css`
    -webkit-tap-highlight-color: transparent;
    transition: color 100ms ease;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    font-size: 18px;
    font-weight: 700;
    height: 100%;
    &:after {
      content: ' ';
      width: 100%;
      height: 3px;
      display: block;
      margin-top: 10px;
    }
  `,
  MobileActiveLink: css`
    width: 100%;
    padding: 12px 32px;
    font-size: 20px;
    font-weight: 500px;
  `,
  ServicesLink: css`
    display: flex;
    justify-content: space-between;
  `,
  Button: css`
    margin-top: 16px;
    padding: 10px 0 10px 0;
    ${MediaQueries.Medium} {
      margin-top: 0;
      width: 160px;
      padding: 13px 0 13px 0;
    }
  `,
};

export default compose(
  withRouter,
  withCurrentUser,
  withBreakpoint,
  withWindowDimensions,
  connect(
    () => ({}),
    dispatch => ({ logout: () => dispatch(currentUserRedux.actions.logout()) })
  )
)(SiteNavigation);



// WEBPACK FOOTER //
// ./src/ui/components/chrome/SiteNavigation.js